import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Plus, Trash2 } from 'lucide-react';
import { usePage } from '../../hooks/usePage';
import { useToast } from '../../hooks/useToast';
import {
  knowledgeDocumentsApi,
  KnowledgeDocument,
} from '../../api/knowledge-documents';
import {
  CATEGORIES_BY_AUDIENCE,
  KnowledgeAudience,
  KnowledgeCategory,
} from '../../api/knowledge-gaps';
import { Card } from '../../components/ui/Card';
import { Table } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { PageHeader } from '../../components/ui/PageHeader';
import { Pagination } from '../../components/ui/Pagination';
import { Spinner } from '../../components/ui/Spinner';

const AUDIENCE_OPTIONS: KnowledgeAudience[] = ['consumer', 'business', 'both'];

const audienceLabel: Record<KnowledgeAudience, string> = {
  consumer: 'Personal app',
  business: 'Business app',
  both: 'Both apps',
};

const formatCategory = (c: string) =>
  c
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-NG', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

const filterSelectStyle: React.CSSProperties = {
  height: 38,
  padding: '0 32px 0 12px',
  fontSize: 13,
  fontFamily: 'var(--font-family)',
  color: 'var(--neutral-700)',
  background: 'var(--neutral-100)',
  border: '1px solid var(--neutral-300)',
  borderRadius: 'var(--radius-md)',
  outline: 'none',
  cursor: 'pointer',
};

const fieldStyle: React.CSSProperties = {
  ...filterSelectStyle,
  width: '100%',
  cursor: 'auto',
};

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--neutral-600)',
};

/**
 * The knowledge base itself, rather than the questions it failed to answer.
 *
 * The gap queue only ever contains things somebody already asked and the
 * assistant already got wrong. This is where the corpus gets written ahead of
 * that — which is the difference between somebody being able to sit down and
 * write the twenty things a Nigerian SME needs to know, and waiting for the
 * assistant to fail in public twenty times first.
 */
export const KnowledgeDocuments: React.FC = () => {
  usePage('Knowledge Base');
  const toast = useToast();

  const [items, setItems] = useState<KnowledgeDocument[]>([]);
  const [meta, setMeta] = useState<{ page: number; totalPages: number; total: number } | null>(
    null,
  );
  const [page, setPage] = useState(1);
  const [audience, setAudience] = useState<KnowledgeAudience | ''>('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<KnowledgeDocument | 'new' | null>(null);
  const [deleting, setDeleting] = useState<KnowledgeDocument | null>(null);
  const [removing, setRemoving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await knowledgeDocumentsApi.list({
        page,
        limit: 20,
        audience: audience || undefined,
        search: search.trim() || undefined,
      });
      setItems(result.items);
      setMeta(result.meta);
    } catch (err) {
      toast.error(err, 'Could not load the knowledge base.');
    } finally {
      setLoading(false);
    }
  }, [page, audience, search, toast]);

  useEffect(() => {
    setPage(1);
  }, [audience, search]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      setRemoving(true);
      await knowledgeDocumentsApi.remove(deleting.id);
      toast.success('Document deleted');
      setDeleting(null);
      await fetchData();
    } catch (err) {
      toast.error(err, 'Could not delete the document.');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Knowledge Base"
        description="What the assistants read before answering. A document is retrieved by meaning, not by keyword, so write it the way somebody would ask about it."
        actions={
          <Button onClick={() => setEditing('new')}>
            <Plus size={14} style={{ marginRight: 6 }} />
            Write a document
          </Button>
        }
      />

      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
        <select
          value={audience}
          onChange={(e) => setAudience(e.target.value as KnowledgeAudience | '')}
          style={filterSelectStyle}
        >
          <option value="">Every audience</option>
          {AUDIENCE_OPTIONS.map((a) => (
            <option key={a} value={a}>
              {audienceLabel[a]}
            </option>
          ))}
        </select>
        <Input
          placeholder="Search titles"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 260 }}
        />
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spinner size={28} />
        </div>
      ) : (
        <Card padding={0}>
          <Table
            emptyMessage="Nothing written yet. Until something is, the assistant answers these questions from memory alone."
            onRowClick={(row: KnowledgeDocument) => setEditing(row)}
            columns={[
              {
                key: 'title',
                header: 'Title',
                render: (r: KnowledgeDocument) => (
                  <div>
                    <div style={{ color: 'var(--neutral-800)', maxWidth: 380 }}>{r.title}</div>
                    {r.source && (
                      <div style={{ fontSize: 11, color: 'var(--neutral-500)', marginTop: 2 }}>
                        {r.source}
                      </div>
                    )}
                  </div>
                ),
              },
              {
                key: 'audience',
                header: 'Read by',
                render: (r: KnowledgeDocument) => (
                  <span style={{ fontSize: 13, color: 'var(--neutral-700)' }}>
                    {audienceLabel[r.audience] ?? r.audience}
                  </span>
                ),
              },
              {
                key: 'category',
                header: 'Category',
                render: (r: KnowledgeDocument) => (
                  <span style={{ fontSize: 13, color: 'var(--neutral-700)' }}>
                    {formatCategory(r.category)}
                  </span>
                ),
              },
              {
                key: 'status',
                header: 'Status',
                render: (r: KnowledgeDocument) =>
                  // Zero chunks means stored but unretrievable — the
                  // embedding pass failed and nothing else on the row says
                  // so. It reads as a working document until somebody
                  // wonders why it never comes back.
                  r.chunkCount === 0 ? (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: 12,
                        color: 'var(--danger-900)',
                      }}
                    >
                      <AlertTriangle size={13} />
                      Not indexed
                    </span>
                  ) : r.isActive ? (
                    <Badge variant="success">Live</Badge>
                  ) : (
                    <Badge variant="neutral">Retired</Badge>
                  ),
              },
              {
                key: 'updatedAt',
                header: 'Updated',
                render: (r: KnowledgeDocument) => formatDate(r.updatedAt),
              },
              {
                key: 'action',
                header: '',
                render: (r: KnowledgeDocument) => (
                  <Button
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleting(r);
                    }}
                  >
                    <Trash2 size={14} />
                  </Button>
                ),
              },
            ]}
            data={items}
          />
          {meta && meta.totalPages > 1 && (
            <Pagination
              page={meta.page}
              totalPages={meta.totalPages}
              total={meta.total}
              onPageChange={setPage}
            />
          )}
        </Card>
      )}

      {editing && (
        <DocumentModal
          document={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void fetchData();
          }}
          toast={toast}
        />
      )}

      <ConfirmDialog
        isOpen={Boolean(deleting)}
        title="Delete this document?"
        message={
          deleting
            ? `"${deleting.title}" and its embeddings go for good. If the answer has only gone stale, retire it instead — it stops being retrieved but stays readable for whoever writes the replacement.`
            : ''
        }
        confirmLabel="Delete"
        variant="danger"
        loading={removing}
        onConfirm={() => void handleDelete()}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
};

const DocumentModal: React.FC<{
  document: KnowledgeDocument | null;
  onClose: () => void;
  onSaved: () => void;
  toast: { success: (msg: string) => void; error: (err: unknown, fallback?: string) => void };
}> = ({ document, onClose, onSaved, toast }) => {
  const [title, setTitle] = useState(document?.title ?? '');
  const [audience, setAudience] = useState<KnowledgeAudience>(
    document?.audience ?? 'business',
  );
  const [content, setContent] = useState(document?.content ?? '');
  const [source, setSource] = useState(document?.source ?? '');
  const [isActive, setIsActive] = useState(document?.isActive ?? true);
  const [saving, setSaving] = useState(false);

  // The categories that fit the audience. Filing a company's VAT guidance
  // under Personal Finance does not misroute it — retrieval keys off
  // audience — but it mislabels it for everyone who reads the list after.
  const categoryOptions = useMemo(
    () => CATEGORIES_BY_AUDIENCE[audience] ?? CATEGORIES_BY_AUDIENCE.consumer,
    [audience],
  );
  const [category, setCategory] = useState<KnowledgeCategory>(
    document?.category ?? CATEGORIES_BY_AUDIENCE.business[0],
  );

  useEffect(() => {
    if (!categoryOptions.includes(category)) setCategory(categoryOptions[0]);
  }, [categoryOptions, category]);

  const canSave = title.trim().length >= 3 && content.trim().length >= 20;
  // Only an edit that changes the words costs a re-embed, and it is the one
  // thing here that is not instant — worth saying before the click, not after.
  const willReindex = Boolean(document) && content !== document?.content;

  const handleSave = async () => {
    if (!canSave) return;
    try {
      setSaving(true);
      if (document) {
        await knowledgeDocumentsApi.update(document.id, {
          title: title.trim(),
          category,
          audience,
          content: content.trim(),
          isActive,
        });
        toast.success(willReindex ? 'Saved and re-indexed' : 'Saved');
      } else {
        await knowledgeDocumentsApi.create({
          title: title.trim(),
          category,
          audience,
          content: content.trim(),
          source: source.trim() || undefined,
        });
        toast.success('Written and indexed');
      }
      onSaved();
    } catch (err) {
      toast.error(err, 'Could not save the document.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={document ? 'Edit document' : 'Write a document'}
      maxWidth={680}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={labelStyle}>Title</label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. VAT registration thresholds for small companies"
          />
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={labelStyle}>Read by</label>
            <select
              value={audience}
              onChange={(e) => setAudience(e.target.value as KnowledgeAudience)}
              style={fieldStyle}
            >
              {AUDIENCE_OPTIONS.map((a) => (
                <option key={a} value={a}>
                  {audienceLabel[a]}
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={labelStyle}>Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as KnowledgeCategory)}
              style={fieldStyle}
            >
              {categoryOptions.map((c) => (
                <option key={c} value={c}>
                  {formatCategory(c)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={labelStyle}>Content</label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={12}
            placeholder="Write it the way somebody would ask about it — retrieval matches on meaning, so a paragraph that uses the words a business owner would use gets found by the question they would ask."
            style={{ ...fieldStyle, height: 'auto', padding: 12, resize: 'vertical' }}
          />
          <span style={{ fontSize: 11, color: 'var(--neutral-500)' }}>
            State dates and figures explicitly — "as of the 2026 Finance Act" beats
            "currently", which stops being true without anybody noticing.
          </span>
        </div>

        {!document && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={labelStyle}>Source (optional)</label>
            <Input
              value={source}
              onChange={(e) => setSource(e.target.value)}
              placeholder="e.g. FIRS circular 2026/03"
            />
          </div>
        )}

        {document && (
          <label
            style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}
          >
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            Live — unchecking retires it from retrieval without deleting it
          </label>
        )}

        {willReindex && (
          <div style={{ fontSize: 12, color: 'var(--neutral-600)' }}>
            The text changed, so this re-indexes on save. Takes a moment.
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void handleSave()} loading={saving} disabled={!canSave}>
            {document ? 'Save' : 'Write it'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
